import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { X } from 'lucide-react';
import ApiService from '../../components/api_services/apiService.jsx';
import {
  setShowAddModal,
  setShowEditModal,
  setSelectedArticle,
} from '../../store/encyclopediaTabSlice';
import './Encyclopedia.css';

function AddArticleModal() {
  const dispatch = useDispatch();
  const showAddModal = useSelector((state) => state.encyclopedia.showAddModal);
  const showEditModal = useSelector((state) => state.encyclopedia.showEditModal);
  const selectedArticle = useSelector((state) => state.encyclopedia.selectedArticle);
  const isEditMode = showEditModal && selectedArticle;

  const [formData, setFormData] = useState({
    title: '',
    street: '',
    street_number: '',
    post_code: '',
    city: '',
    pin_url: '',
    description: '',
    photo_paths: [],
    latitude: '',
    longitude: '',
  });
  const [loading, setLoading] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const fileInputRef = React.useRef(null);

  const extractCoordinatesFromGoogleMapsUrl = (rawUrl) => {
    if (!rawUrl || typeof rawUrl !== 'string') return null;
    const value = rawUrl.trim();

    const toNumberPair = (latStr, lngStr) => {
      const lat = Number(latStr);
      const lng = Number(lngStr);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
      return { latitude: String(lat), longitude: String(lng) };
    };

    const directPair = value.match(/(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)/);
    if (directPair) {
      const parsed = toNumberPair(directPair[1], directPair[2]);
      if (parsed) return parsed;
    }

    try {
      const url = new URL(value);

      // Example: https://maps.google.com/?q=52.2297,21.0122
      const q = url.searchParams.get('q') || url.searchParams.get('query');
      if (q) {
        const match = q.match(/(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)/);
        if (match) {
          const parsed = toNumberPair(match[1], match[2]);
          if (parsed) return parsed;
        }
      }

      // Example: .../@52.2297,21.0122,17z
      const atMatch = url.pathname.match(/@(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/);
      if (atMatch) {
        const parsed = toNumberPair(atMatch[1], atMatch[2]);
        if (parsed) return parsed;
      }
    } catch (error) {
      // Non-URL input; regex fallbacks below.
    }

    // Example internals from some Google URLs: ...!3d52.2297!4d21.0122...
    const encodedMatch = value.match(/!3d(-?\d+(?:\.\d+)?)!4d(-?\d+(?:\.\d+)?)/);
    if (encodedMatch) {
      return toNumberPair(encodedMatch[1], encodedMatch[2]);
    }

    return null;
  };

  useEffect(() => {
    if (isEditMode && selectedArticle?.id) {
      loadArticleForEdit();
    }
  }, [isEditMode, selectedArticle]);

  const loadArticleForEdit = async () => {
    try {
      const article = await ApiService.getArticleById(selectedArticle.id);
      setFormData({
        title: article.title || '',
        street: article.street || '',
        street_number: article.street_number || '',
        post_code: article.post_code || '',
        city: article.city || '',
        pin_url: article.pin_url || '',
        description: article.description || '',
        photo_paths: article.photo_paths || [],
        latitude: article.latitude ?? '',
        longitude: article.longitude ?? '',
      });
    } catch (error) {
      console.error('Błąd podczas ładowania artykułu do edycji:', error);
      alert('Nie udało się załadować danych artykułu.');
    }
  };

  const handleClose = () => {
    if (isEditMode) {
      dispatch(setShowEditModal(false));
    } else {
      dispatch(setShowAddModal(false));
    }
    dispatch(setSelectedArticle(null));
    setFormData({
      title: '',
      street: '',
      street_number: '',
      post_code: '',
      city: '',
      pin_url: '',
      description: '',
      photo_paths: [],
      latitude: '',
      longitude: '',
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isEditMode) {
        await ApiService.updateArticle(selectedArticle.id, formData);
        alert('Artykuł został zaktualizowany. Status został zresetowany do PENDING i wymaga ponownej akceptacji.');
      } else {
        const payload = {
          title: formData.title.trim(),
          city: formData.city.trim(),
          street: formData.street.trim(),
          street_number: formData.street_number.trim(),
          post_code: formData.post_code.trim(),
          pin_url: formData.pin_url?.trim() || undefined,
          description: formData.description.trim(),
          photo_paths: formData.photo_paths || [],
          latitude: Number(formData.latitude),
          longitude: Number(formData.longitude),
        };

        if (!Number.isFinite(payload.latitude) || !Number.isFinite(payload.longitude)) {
          alert('Nie udało się odczytać współrzędnych z linku Google Maps. Użyj pełnego linku zawierającego współrzędne (np. z @lat,lng albo ?q=lat,lng).');
          setLoading(false);
          return;
        }

        await ApiService.createArticle(payload);
        alert('Artykuł został utworzony i wysłany do weryfikacji.');
      }
      handleClose();
      // Reload list - would need to trigger parent refresh
      window.location.reload(); // Temporary solution
    } catch (error) {
      console.error('Błąd podczas zapisywania:', error);
      alert('Nie udało się zapisać artykułu.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => {
      const next = {
        ...prev,
        [field]: value,
      };

      if (field === 'pin_url') {
        const coords = extractCoordinatesFromGoogleMapsUrl(value);
        if (coords) {
          next.latitude = coords.latitude;
          next.longitude = coords.longitude;
        }
      }

      return next;
    });
  };

  const normalizeUploadedImageId = (response) => {
    const normalizeStringValue = (raw) => {
      if (!raw || typeof raw !== 'string') return null;
      const value = raw.trim();
      if (!value) return null;

      // Full URL or path => keep only hash segment after /images/
      if (value.includes('/images/')) {
        const afterImages = value.split('/images/').pop();
        if (afterImages) return afterImages.split('?')[0].split('#')[0];
      }

      // Generic URL/path fallback => last path segment
      if (value.startsWith('http://') || value.startsWith('https://') || value.startsWith('/')) {
        const clean = value.split('?')[0].split('#')[0];
        const parts = clean.split('/').filter(Boolean);
        if (parts.length) return parts[parts.length - 1];
      }

      return value;
    };

    if (typeof response === 'string') return normalizeStringValue(response);
    if (response && typeof response === 'object') {
      // Prefer hash/path-like fields over numeric IDs.
      const hashCandidate =
        normalizeStringValue(response.hash) ||
        normalizeStringValue(response.hashedImageId) ||
        normalizeStringValue(response.imageHash) ||
        normalizeStringValue(response.photo_path) ||
        normalizeStringValue(response.photoPath) ||
        normalizeStringValue(response.path) ||
        normalizeStringValue(response.url) ||
        normalizeStringValue(response.imageUrl);

      if (hashCandidate) return hashCandidate;

      const idCandidate =
        normalizeStringValue(response.imageId) ||
        normalizeStringValue(response.id);

      if (idCandidate) return idCandidate;
    }
    return null;
  };

  const handleSelectImagesClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    setUploadingImages(true);
    try {
      const uploadedIds = [];
      for (const file of files) {
        const uploadResponse = await ApiService.uploadImage(file);
        const imageId = normalizeUploadedImageId(uploadResponse);
        if (!imageId) {
          throw new Error('Nieprawidłowa odpowiedź uploadu obrazka.');
        }
        uploadedIds.push(imageId);
      }

      setFormData((prev) => ({
        ...prev,
        photo_paths: [...new Set([...(prev.photo_paths || []), ...uploadedIds])],
      }));
    } catch (error) {
      console.error('Błąd podczas uploadu zdjęć:', error);
      alert('Nie udało się przesłać zdjęć.');
    } finally {
      setUploadingImages(false);
      event.target.value = '';
    }
  };

  const handleRemoveImage = (imageId) => {
    setFormData((prev) => ({
      ...prev,
      photo_paths: (prev.photo_paths || []).filter((id) => id !== imageId),
    }));
  };

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content encyclopedia-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={handleClose}>
          <X size={20} />
        </button>

        <h2 className="modal-title">{isEditMode ? 'Edytuj wpis' : 'Dodaj wpis'}</h2>

        <form onSubmit={handleSubmit} className="encyclopedia-form">
          <div className="form-field">
            <label>Nazwa</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder="Parkowanie Berlin"
              required
            />
          </div>

          <div className="form-field">
            <label>Adres ulica, numer</label>
            <div className="form-field-row">
              <input
                type="text"
                value={formData.street}
                onChange={(e) => handleChange('street', e.target.value)}
                placeholder="Ulica"
              />
              <input
                type="text"
                value={formData.street_number}
                onChange={(e) => handleChange('street_number', e.target.value)}
                placeholder="00"
                style={{ width: '80px' }}
              />
            </div>
          </div>

          <div className="form-field">
            <label>Kod pocztowy</label>
            <input
              type="text"
              value={formData.post_code}
              onChange={(e) => handleChange('post_code', e.target.value)}
              placeholder="00-000"
            />
          </div>

          <div className="form-field">
            <label>Miasto</label>
            <input
              type="text"
              value={formData.city}
              onChange={(e) => handleChange('city', e.target.value)}
              placeholder="Miasto"
            />
          </div>

          <div className="form-field">
            <label>Pinezka (wklej link)</label>
            <input
              type="text"
              value={formData.pin_url}
              onChange={(e) => handleChange('pin_url', e.target.value)}
              placeholder="https://www.google.com/maps/..."
            />
            {formData.latitude && formData.longitude && (
              <div className="coords-resolved-hint">
                Współrzędne odczytane: {formData.latitude}, {formData.longitude}
              </div>
            )}
          </div>

          <div className="form-field">
            <label>Opis</label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Uzupełnij"
              rows={6}
            />
          </div>

          <div className="form-field">
            <label>Zdjęcia</label>
            <div className="photo-upload-area">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                style={{ display: 'none' }}
                onChange={handleFileUpload}
              />
              <button
                type="button"
                className="photo-placeholder"
                onClick={handleSelectImagesClick}
                disabled={uploadingImages}
              >
                <span>+</span>
                <span>{uploadingImages ? 'Przesyłanie...' : 'Dodaj zdjęcie'}</span>
              </button>
              {!!formData.photo_paths?.length && (
                <div className="uploaded-images-list">
                  {formData.photo_paths.map((imageId) => (
                    <div key={imageId} className="uploaded-image-item">
                      <span className="uploaded-image-id">{imageId}</span>
                      <button
                        type="button"
                        className="uploaded-image-remove"
                        onClick={() => handleRemoveImage(imageId)}
                      >
                        Usuń
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn-cancel" onClick={handleClose}>
              Anuluj
            </button>
            <button type="submit" className="btn-submit" disabled={loading}>
              {isEditMode ? 'Zapisz zmiany' : 'Dodaj wpis'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddArticleModal;




